import { Link } from "react-router";
import { X } from "lucide-react";
import type { MonthlySummary } from "@/lib/expenses";
import { colorOf } from "@/lib/palette";
import { useTaxonomy } from "@/lib/taxonomy";
import { formatMoney } from "@/lib/format";

// The three-bucket rollup (personal / business / one-off), plus the per-scope
// ranking. Which scopes land in which bucket is the tenant's configuration, so
// the bands are named after the kinds rather than after any one scope. Each row
// links back into the dashboard as a ?scope= filter.
export function ScopeSplit({
  summary,
  month,
  active,
  category,
}: {
  summary: MonthlySummary;
  month: string;
  active?: string;
  category?: string;
}) {
  const { scopes, scopeColor, scopeLabel, excludedLabel } = useTaxonomy();
  const { personalCents, businessCents, excludedCents, byScope, currency } =
    summary;
  const grandTotal = personalCents + businessCents + excludedCents;
  if (grandTotal === 0) return null;

  const link = (scope?: string) => {
    const params = new URLSearchParams({ month });
    if (category) params.set("category", category);
    if (scope) params.set("scope", scope);
    return `/?${params.toString()}`;
  };

  // Each band borrows the colour of the first scope of that kind, so the bar
  // and the list below agree without a second palette.
  const hexOfKind = (kind: string, fallback: string) =>
    colorOf(scopes.find((s) => s.kind === kind)?.color ?? fallback).hex;

  const bands = [
    { key: "personal", label: "Personal", cents: personalCents, hex: hexOfKind("personal", "zinc") },
    { key: "business", label: "Business", cents: businessCents, hex: hexOfKind("business", "blue") },
    { key: "excluded", label: excludedLabel, cents: excludedCents, hex: hexOfKind("excluded", "amber") },
  ].filter((b) => b.cents > 0);

  return (
    <div className="rounded-[1.75rem] bg-card p-5 shadow-sm ring-1 ring-black/[0.05]">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Personal vs business</h2>
        {active && (
          <Link
            to={link()}
            className="flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground active:bg-border"
          >
            <X className="size-3" />
            Clear
          </Link>
        )}
      </div>

      <div className="flex h-2.5 overflow-hidden rounded-full bg-muted">
        {bands.map((b) => (
          <div
            key={b.key}
            style={{
              width: `${(b.cents / grandTotal) * 100}%`,
              background: b.hex,
            }}
          />
        ))}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        {bands.map((b) => (
          <div key={b.key}>
            <div className="flex items-center gap-1.5">
              <span
                className="size-2 shrink-0 rounded-full"
                style={{ background: b.hex }}
              />
              <span className="text-xs text-muted-foreground">{b.label}</span>
            </div>
            <p className="mt-0.5 text-sm font-semibold tabular-nums">
              {formatMoney(b.cents, currency)}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-1 border-t border-border/70 pt-3">
        {byScope.map((s) => {
          const color = scopeColor(s.scope);
          const isActive = active === s.scope;
          const pct = Math.round((s.totalCents / grandTotal) * 100);
          return (
            <Link
              key={s.scope}
              to={isActive ? link() : link(s.scope)}
              className={`flex items-center gap-2.5 rounded-xl px-2 py-1.5 transition-colors active:bg-muted ${
                isActive ? "bg-muted" : ""
              }`}
            >
              <span className={`size-2 shrink-0 rounded-full ${color.dot}`} />
              <span className="min-w-0 flex-1 truncate text-sm">
                {scopeLabel(s.scope)}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                {pct}% · {s.count}
              </span>
              <span className="shrink-0 text-sm font-semibold tabular-nums">
                {formatMoney(s.totalCents, currency)}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
